import { getPhoneSchema } from "Schemas/phoneSchema";
import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

const RESOURCES =
  Resources.Components.Merchant.Facilities.Form.OpeningHours.Validation;

// Time format: HH:MM:SS (24-hour format)
const TIME_FORMAT_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;

// Schema for MerchantFacilityHoursDto
const getMerchantFacilityHoursSchema = (t: TFunction) => {
  const baseSchema = z.object({
    weekday: z.number().min(0).max(6),
    openTime: z.string().nullable(),
    closeTime: z.string().nullable(),
    isOpenAllDay: z.boolean(),
    isClosedAllDay: z.boolean(),
  });

  return baseSchema
    .refine((data) => !(data.isOpenAllDay && data.isClosedAllDay), {
      message: t(RESOURCES.CannotBeBothOpenAndClosed),
      path: ["isOpenAllDay"],
    })
    .refine(
      (data) => {
        // Validate time format if present
        if (data.openTime && !TIME_FORMAT_REGEX.test(data.openTime))
          return false;
        if (data.closeTime && !TIME_FORMAT_REGEX.test(data.closeTime))
          return false;
        return true;
      },
      {
        message: t(RESOURCES.InvalidTimeFormat),
        path: ["openTime"],
      },
    )
    .refine(
      (data) => {
        // Note: This validation is also performed in the UI (TimeUtils.checkTimeRangeValidity)
        // for immediate user feedback. Schema validation provides an additional layer of
        // protection before form submission.
        // Validate openTime < closeTime when both are present
        if (data.openTime && data.closeTime) {
          return data.openTime < data.closeTime;
        }
        return true;
      },
      {
        message: t(RESOURCES.OpeningTimeBeforeClosingTime),
        path: ["openTime"],
      },
    );
};

// Schema for MerchantFacilitySpecialHoursDto
const merchantFacilitySpecialHoursSchema = z.object({
  date: z.string(),
  openTime: z.string().nullable(),
  closeTime: z.string().nullable(),
  isClosedAllDay: z.boolean(),
  isOpenAllDay: z.boolean(),
});

const addressValidationSchema = z.object({
  addressID: z.number().nullish(),
  street: z.string().nullish(),
  streetNumber: z.string().nullish(),
  orientationNumber: z.string().nullish(),
  municipality: z.string().nullish(),
  postalCode: z.string().nullish(),
});

/** Optional email — empty string is allowed, but non-empty must be valid */
const optionalEmailSchema = z
  .string()
  .nullish()
  .refine((val) => !val || z.string().email().safeParse(val).success, {
    message: "Invalid email",
  });

// A count is required only while its field is shown; the hidden one keeps whatever the facility has (often null).
type SchemaOptions = {
  hasOrbitLProduct?: boolean;
  hasSoftPosProduct?: boolean;
};

export const getMerchantFacilitySchema = (
  t: TFunction,
  options?: SchemaOptions,
) =>
  z
    .object({
      publicID: z.string().nullish(),
      name: z.string().min(1),
      address: addressValidationSchema.nullish(),
      providedServices: z.string().nullish(),
      responsiblePerson: z.string().min(1),
      phone: getPhoneSchema(t, { isRequired: false }),
      email: optionalEmailSchema,
      yearlyTurnover: z.number().nullish(),
      realCardTurnover: z.number().nullish(),
      estimatedTurnover: z.number().nullish(),
      averageTransactionValue: z.number().nullish(),
      terminalCount: options?.hasOrbitLProduct
        ? z.number().min(0)
        : z.number().min(0).nullish(),
      softPosCount: options?.hasSoftPosProduct
        ? z.number().min(0)
        : z.number().min(0).nullish(),
      isPublic: z.boolean().nullish(),
      hours: z.array(getMerchantFacilityHoursSchema(t)).nullish(),
      specialHours: z.array(merchantFacilitySpecialHoursSchema).nullish(),
      categoryCodes: z.array(z.string()).nullish(),
      description: z.string().max(512).nullish(),
    })
    .refine(
      (data) => {
        if (!data.isPublic) {
          return true;
        }
        return !!data.categoryCodes && data.categoryCodes.length > 0;
      },
      {
        message: t(
          Resources.Components.Merchant.Facilities.Form.Validation
            .CategoryRequiredWhenPublic,
        ),
        path: ["categoryCodes"],
      },
    );

export type FacilityFormModel = z.infer<
  ReturnType<typeof getMerchantFacilitySchema>
>;

export type FacilitySection =
  | "basicInformation"
  | "contact"
  | "publicFacilityInfo"
  | "address"
  | "turnover"
  | "terminals"
  | "photos";
